import React, { useState } from "react";
import {
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { Plus, Trash2, Edit, Check, X } from "lucide-react";
import ProductSection from "../ProductSection";

// Common product description fields
const COMMON_DESCRIPTION_FIELDS = [
  "Fabric",
  "Length",
  "Sleeve Type",
  "Neckline",
  "Embellishments",
  "Weight",
  "Care Instructions",
  "Country of Origin",
];

const ProductDescription = ({ productData, updateProductData, errors }) => {
  const [showAddDescription, setShowAddDescription] = useState(false);
  const [descriptionData, setDescriptionData] = useState({
    title: "",
    description: "",
  });
  const [editingIndex, setEditingIndex] = useState(null);

  const handleDescriptionChange = (field, value) => {
    setDescriptionData({
      ...descriptionData,
      [field]: value,
    });
  };

  const handleAddDescription = () => {
    if (editingIndex !== null) {
      // Update existing description
      const updatedDescriptions = [...productData.productDescription];
      updatedDescriptions[editingIndex] = descriptionData;
      updateProductData({ productDescription: updatedDescriptions });
      setEditingIndex(null);
    } else {
      // Add new description
      updateProductData({
        productDescription: [
          ...productData.productDescription,
          descriptionData,
        ],
      });
    }

    // Reset form
    setDescriptionData({
      title: "",
      description: "",
    });
    setShowAddDescription(false);
  };

  const handleEditDescription = index => {
    setDescriptionData(productData.productDescription[index]);
    setEditingIndex(index);
    setShowAddDescription(true);
  };

  const handleDeleteDescription = index => {
    const updatedDescriptions = [...productData.productDescription];
    updatedDescriptions.splice(index, 1);
    updateProductData({ productDescription: updatedDescriptions });
  };

  const handleQuickAdd = title => {
    setDescriptionData({
      ...descriptionData,
      title,
    });
    setShowAddDescription(true);
  };

  // Filter out already used fields
  const availableQuickAddFields = COMMON_DESCRIPTION_FIELDS.filter(
    field => !productData.productDescription.some(desc => desc.title === field),
  );

  return (
    <div className="space-y-6">
      <ProductSection data="Product Description" />

      <div className="border border-gray-200 rounded-lg p-4">
        {/* Quick add common fields */}
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-2">Quick Add Common Fields:</p>
          <div className="flex flex-wrap gap-1">
            {availableQuickAddFields.map(field => (
              <Chip
                key={field}
                label={field}
                size="small"
                onClick={() => handleQuickAdd(field)}
                sx={{
                  fontFamily: "Outfit",
                  fontSize: "0.75rem",
                  backgroundColor: "#F9F8FE",
                  color: "#5652B0",
                  "&:hover": {
                    backgroundColor: "#EEEDF7",
                  },
                }}
              />
            ))}
          </div>
        </div>

        {productData.productDescription.length === 0 && !showAddDescription ? (
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <p className="text-gray-500 text-sm mb-2">
              No description fields added yet
            </p>
            <button
              onClick={() => {
                setShowAddDescription(true);
                setEditingIndex(null);
              }}
              className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-xs font-medium hover:bg-[#F9F8FE]">
              Add Description Field
            </button>
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: "0.5rem" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      width: "30%",
                    }}>
                    Title
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}>
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      width: "100px",
                    }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productData.productDescription.map((desc, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      {desc.title}
                    </TableCell>
                    <TableCell
                      sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                      {desc.content}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <IconButton
                          size="small"
                          onClick={() => handleEditDescription(index)}
                          sx={{ color: "#5652B0", padding: "4px" }}>
                          <Edit size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDescription(index)}
                          sx={{ color: "#d32f2f", padding: "4px" }}>
                          <Trash2 size={14} />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {showAddDescription && (
                  <TableRow>
                    <TableCell>
                      <TextField
                        placeholder="Title"
                        value={descriptionData.title}
                        onChange={e =>
                          handleDescriptionChange("title", e.target.value)
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Description"
                        value={descriptionData.description}
                        onChange={e =>
                          handleDescriptionChange("description", e.target.value)
                        }
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        InputProps={{
                          sx: {
                            borderRadius: "0.5rem",
                            fontFamily: "Outfit",
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <IconButton
                          size="small"
                          onClick={handleAddDescription}
                          disabled={
                            !descriptionData.title ||
                            !descriptionData.description
                          }
                          sx={{
                            color: "#5652B0",
                            padding: "4px",
                            "&.Mui-disabled": {
                              color: "rgba(0, 0, 0, 0.26)",
                            },
                          }}>
                          <Check size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowAddDescription(false);
                            setEditingIndex(null);
                            setDescriptionData({ title: "", description: "" });
                          }}
                          sx={{ color: "#d32f2f", padding: "4px" }}>
                          <X size={14} />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!showAddDescription && productData.productDescription.length > 0 && (
          <button
            onClick={() => {
              setShowAddDescription(true);
              setEditingIndex(null);
              setDescriptionData({ title: "", description: "" });
            }}
            className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-xs font-medium hover:bg-[#F9F8FE] mt-3 flex items-center gap-1">
            <Plus size={14} />
            Add Description
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductDescription;
